import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, User, Users } from 'lucide-react';

export default function AssigneePicker({
  value,
  onChange,
  agents = [],
  isAdmin = false,
  directOnly = false,
  currentUserId = null,
}) {
  const teammates = (agents || []).filter((a) => !currentUserId || String(a._id) !== String(currentUserId));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (!ref.current?.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const allLabel = directOnly ? 'All Direct Teammates' : 'All Accessible Leads';
  const label =
    value === 'me' ? 'My Own Records' :
    value === 'all' ? allLabel :
    agents.find((a) => a._id === value)?.name || 'Agent';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="bg-white/5 hover:bg-white/[0.07] border border-white/10 rounded-lg px-3 py-2 flex items-center gap-2 text-sm cursor-pointer transition"
      >
        <span className="text-white/90 font-medium">{label}</span>
        <X
          className="w-3 h-3 text-white/40 hover:text-white transition"
          onClick={(e) => { e.stopPropagation(); onChange('all'); }}
        />
        <ChevronDown className={`w-4 h-4 text-white/40 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-[#0a0a1f] border border-white/15 ring-1 ring-black/40 rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden z-50">
          <div className="max-h-[360px] overflow-y-auto py-1">
            <button
              onClick={() => { onChange('me'); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 cursor-pointer ${value === 'me' ? 'text-civix-cyan font-semibold' : 'text-white/90'}`}
            >
              <User className="w-3.5 h-3.5 text-civix-cyan/80" />
              <span>My Own Records</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => { onChange('all'); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 cursor-pointer ${value === 'all' ? 'text-civix-cyan font-semibold' : 'text-white/80'}`}
              >
                <Users className="w-3.5 h-3.5 text-civix-purpleSoft" />
                <span>{allLabel}</span>
              </button>
            )}
            {isAdmin && <div className="border-t border-white/10 my-1" />}
            {isAdmin && teammates.length === 0 && (
              <div className="px-3 py-2 text-xs text-white/40 italic">No direct teammates added</div>
            )}
            {teammates.map((a) => (
              <button
                key={a._id}
                onClick={() => { onChange(a._id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between cursor-pointer ${value === a._id ? 'text-civix-cyan font-semibold' : 'text-white/90'}`}
              >
                <span className="truncate">{a.name || a.email}</span>
                {a.role && a.role !== 'agent' && (
                  <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-white/10 text-white/60 ml-2">
                    {a.role}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
